import { Schema, Effect } from "effect";

const NonEmptyInputDomain = Schema.String.pipe(
  Schema.check(Schema.makeFilter((domain) => domain.trim().length > 0))
);

const LookupOffset = Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0)));
const LookupLimit = Schema.Int.pipe(
  Schema.check(Schema.isGreaterThan(0)),
  Schema.check(Schema.isLessThanOrEqualTo(100))
);

/** Bounded batch request for resolving hiring evidence across submitted domains. */
export const CareerDomainLookupRequestSchema = Schema.Struct({
  domains: Schema.Array(NonEmptyInputDomain).pipe(
    Schema.check(Schema.isMinLength(1)),
    Schema.check(Schema.isMaxLength(20))
  ),
  adsOffset: LookupOffset.pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => 0)),
    Schema.withConstructorDefault(Effect.sync(() => 0))
  ),
  adsLimit: LookupLimit.pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => 20)),
    Schema.withConstructorDefault(Effect.sync(() => 20))
  ),
  companiesOffset: LookupOffset.pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => 0)),
    Schema.withConstructorDefault(Effect.sync(() => 0))
  ),
  companiesLimit: LookupLimit.pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => 20)),
    Schema.withConstructorDefault(Effect.sync(() => 20))
  ),
});

const CareerDomainLookupCompanyFields = {
  orgnr: Schema.String,
  companyId: Schema.String,
  name: Schema.NullOr(Schema.String),
  url: Schema.NullOr(Schema.String),
  relationship: Schema.Literals(["company", "group"]),
  sourceOrgnr: Schema.String,
  evidence: Schema.optional(
    Schema.Struct({
      method: Schema.optional(Schema.String),
      verifiedAt: Schema.optional(Schema.String),
      url: Schema.optional(Schema.String),
    })
  ),
};

const CareerDomainLookupPage = {
  offset: LookupOffset,
  limit: LookupLimit,
  total: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  nextOffset: Schema.optional(LookupOffset),
  cohortRevision: Schema.optional(Schema.String),
};

const CareerDomainLookupVerifiedCompanySchema = Schema.Struct({
  ...CareerDomainLookupCompanyFields,
  verified: Schema.Literal(true),
});

const CareerDomainLookupCandidateSchema = Schema.Struct({
  ...CareerDomainLookupCompanyFields,
  verified: Schema.Literal(false),
});

const CareerDomainLookupAdSchema = Schema.Struct({
  id: Schema.String,
  title: Schema.NullOr(Schema.String),
  location: Schema.NullOr(Schema.String),
  sourceUrl: Schema.NullOr(Schema.String),
  applyUrl: Schema.NullOr(Schema.String),
  firstSeenAt: Schema.String,
  lastSeenAt: Schema.String,
});

const CareerDomainLookupKeywordSchema = Schema.Struct({
  keyword: Schema.String,
  adCount: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  /** Fraction of analyzed ads, where 0.2 represents 20%. */
  density: Schema.Number.pipe(
    Schema.check(Schema.isGreaterThanOrEqualTo(0)),
    Schema.check(Schema.isLessThanOrEqualTo(1))
  ),
});

const CareerDomainLookupObservationSchema = Schema.Struct({
  scope: Schema.Literals(["company", "group"]),
  asOf: Schema.String,
  observedCount: Schema.optional(Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0)))),
  quality: Schema.Literals(["exact", "lower_bound", "unknown"]),
  reason: Schema.optional(Schema.String),
});

const CareerDomainLookupCollectionSchema = Schema.Struct({
  lastAttemptAt: Schema.optional(Schema.String),
  status: Schema.optional(Schema.Literals(["collecting", "complete", "partial", "failed"])),
  coverage: Schema.Literals(["complete", "partial", "unknown"]),
  warnings: Schema.Array(Schema.String),
});

const CareerDomainLookupResultSchema = Schema.Struct({
  input: Schema.String,
  domain: Schema.NullOr(Schema.String),
  status: Schema.Literals(["known", "unknown", "invalid"]),
  attribution: Schema.Literals(["verified", "unresolved", "ambiguous"]),
  /** Human-readable attribution status; optional so older servers remain decodable. */
  attributionDescription: Schema.optional(Schema.String),
  companies: Schema.Struct({
    ...CareerDomainLookupPage,
    items: Schema.Array(CareerDomainLookupVerifiedCompanySchema),
  }),
  candidates: Schema.Struct({
    ...CareerDomainLookupPage,
    items: Schema.Array(CareerDomainLookupCandidateSchema),
  }),
  ads: Schema.Struct({
    ...CareerDomainLookupPage,
    items: Schema.Array(CareerDomainLookupAdSchema),
  }),
  keywords: Schema.Struct({
    items: Schema.Array(CareerDomainLookupKeywordSchema),
    total: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
    truncated: Schema.Boolean,
    analyzedAds: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  }),
  collection: CareerDomainLookupCollectionSchema,
  observation: Schema.optional(CareerDomainLookupObservationSchema),
});

/** Lookup results preserve the submitted domain and keep candidate attribution separate. */
export const CareerDomainLookupResponseSchema = Schema.Struct({
  results: Schema.Array(CareerDomainLookupResultSchema).pipe(Schema.check(Schema.isMaxLength(20))),
});

export type CareerDomainLookupRequest = Schema.Schema.Type<typeof CareerDomainLookupRequestSchema>;
export type CareerDomainLookupResponse = Schema.Schema.Type<
  typeof CareerDomainLookupResponseSchema
>;
